import { useEffect, useState } from 'react'
import type { MacroConditions } from '../../types/research'
import { getMacroConditions } from '../../lib/macro-api'

// Current macro backdrop for the Marker Lab: the same forces the macro marker
// group (fed-rate, fed-shock, jobs-surprise, usd-fx) tests for influence, shown
// as today's readings — Fed stance, the labor market, and the dollar.

function fmtDate(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00`)
  if (isNaN(d.getTime())) return isoDate
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function daysUntil(isoDate: string): number {
  return Math.round((new Date(`${isoDate}T00:00:00`).getTime() - Date.now()) / 86400000)
}

function inDaysLabel(isoDate: string): string {
  const d = daysUntil(isoDate)
  if (d === 0) return 'today'
  if (d === 1) return 'tomorrow'
  if (d > 1) return `in ${d} days`
  return `${Math.abs(d)} days ago`
}

function LiveBadge({ live }: { live: boolean }) {
  return live ? (
    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">
      LIVE
    </span>
  ) : (
    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200/70 dark:bg-slate-700/60 text-slate-500 dark:text-slate-400">
      SAMPLE
    </span>
  )
}

function ChangeChip({ pct }: { pct: number | null }) {
  if (pct == null) return null
  const positive = pct >= 0
  return (
    <span
      className={`text-xs font-semibold ${
        positive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
      }`}
    >
      {positive ? '+' : ''}
      {pct.toFixed(2)}%
    </span>
  )
}

/** Dovish ←→ hawkish gauge, stance in [−1, 1]. */
function StanceGauge({ stance }: { stance: number }) {
  const pos = Math.min(100, Math.max(0, (stance + 1) * 50))
  return (
    <div>
      <div className="relative h-2 rounded-full bg-gradient-to-r from-sky-400/60 via-slate-300/60 dark:via-slate-600/60 to-amber-500/60">
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-white dark:bg-slate-200 border-2 border-slate-600 dark:border-slate-300 shadow"
          style={{ left: `${pos}%` }}
        />
      </div>
      <div className="flex justify-between text-[10px] text-slate-500 dark:text-slate-400 mt-1">
        <span>Dovish</span>
        <span>Hawkish</span>
      </div>
    </div>
  )
}

export default function MacroConditionsPanel() {
  const [data, setData] = useState<MacroConditions | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    getMacroConditions()
      .then((m) => {
        if (!cancelled) setData(m)
      })
      .catch(() => {
        /* panel stays hidden */
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 animate-pulse">
        <div className="h-5 w-40 bg-slate-200 dark:bg-slate-700 rounded mb-3" />
        <div className="grid sm:grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 bg-slate-200 dark:bg-slate-700 rounded" />
          ))}
        </div>
      </div>
    )
  }
  if (!data) return null

  const { fed, jobs } = data
  const jobsTone =
    jobs.sentiment === 'strong'
      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
      : jobs.sentiment === 'soft'
        ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
        : 'bg-slate-200/70 dark:bg-slate-700/60 text-slate-600 dark:text-slate-300'

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Macro conditions</h3>
        <span className="text-[11px] text-slate-500 dark:text-slate-400">
          {data.fedJobsCurated
            ? 'FX & market data live from the quote feed · Fed & jobs figures are curated samples until the macro route ships'
            : `As of ${new Date(data.asOf).toLocaleString()}`}
        </span>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
        Today's readings behind the macro marker group — the fed-rate, Fed-surprise, jobs-surprise, and USD/FX
        markers ranked in the influence table below.
      </p>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Fed */}
        <div className="bg-slate-100/70 dark:bg-slate-900/50 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-900 dark:text-white">Federal Reserve</span>
            <span
              className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                fed.stance > 0.2
                  ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
                  : fed.stance < -0.2
                    ? 'bg-sky-500/15 text-sky-700 dark:text-sky-300'
                    : 'bg-slate-200/70 dark:bg-slate-700/60 text-slate-600 dark:text-slate-300'
              }`}
            >
              {fed.stanceLabel}
            </span>
          </div>
          <div className="flex items-baseline gap-3 mb-2">
            <span className="text-xl font-semibold text-slate-900 dark:text-white">
              {fed.targetRangePct[0].toFixed(2)}–{fed.targetRangePct[1].toFixed(2)}%
            </span>
            <span className="text-[11px] text-slate-500 dark:text-slate-400">
              target range · effective {fed.effectiveRatePct.toFixed(2)}%
            </span>
          </div>
          <div className="mb-2">
            <StanceGauge stance={fed.stance} />
          </div>
          <p className="text-[11px] text-slate-600 dark:text-slate-300 mb-2">{fed.note}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
            <span>
              Last decision: <span className="text-slate-700 dark:text-slate-200">{fmtDate(fed.lastDecisionDate)}</span>{' '}
              ({fed.lastMove === 'hold' ? 'held' : `${fed.lastMove} ${Math.abs(fed.lastMoveBps)}bps`})
            </span>
            <span>
              Next FOMC:{' '}
              <span className="text-slate-700 dark:text-slate-200">
                {fmtDate(fed.nextMeetingDate)} · {inDaysLabel(fed.nextMeetingDate)}
              </span>
            </span>
          </div>
        </div>

        {/* Jobs */}
        <div className="bg-slate-100/70 dark:bg-slate-900/50 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-900 dark:text-white">Labor market</span>
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold capitalize ${jobsTone}`}>
              {jobs.sentiment}
            </span>
          </div>
          <div className="flex items-baseline gap-3 mb-1">
            <span className="text-xl font-semibold text-slate-900 dark:text-white">
              {jobs.nonfarmPayrollsK >= 0 ? '+' : ''}
              {jobs.nonfarmPayrollsK}K
            </span>
            <span className="text-[11px] text-slate-500 dark:text-slate-400">
              nonfarm payrolls vs {jobs.consensusK}K consensus (
              <span className={jobs.surpriseK >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}>
                {jobs.surpriseK >= 0 ? '+' : ''}
                {jobs.surpriseK}K
              </span>
              )
            </span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400 mb-2">
            <span>
              Unemployment: <span className="text-slate-700 dark:text-slate-200">{jobs.unemploymentRatePct.toFixed(1)}%</span>
            </span>
            <span>
              Avg hourly earnings: <span className="text-slate-700 dark:text-slate-200">{jobs.avgHourlyEarningsYoYPct.toFixed(1)}% YoY</span>
            </span>
          </div>
          <p className="text-[11px] text-slate-600 dark:text-slate-300 mb-2">{jobs.note}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
            <span>
              Last report: <span className="text-slate-700 dark:text-slate-200">{fmtDate(jobs.lastReportDate)}</span>
            </span>
            <span>
              Next report:{' '}
              <span className="text-slate-700 dark:text-slate-200">
                {fmtDate(jobs.nextReportDate)} · {inDaysLabel(jobs.nextReportDate)}
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Dollar & FX */}
      <div className="mt-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-semibold text-slate-900 dark:text-white">Dollar & FX</span>
          <LiveBadge live={data.fx.some((f) => f.live)} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {data.dollarIndex && (
            <div className="bg-slate-100/70 dark:bg-slate-900/50 rounded-lg p-2.5">
              <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate" title={data.dollarIndex.label}>
                DXY
              </p>
              <p className="text-sm font-semibold text-slate-900 dark:text-white">
                {data.dollarIndex.value?.toFixed(2) ?? '—'}
              </p>
              <ChangeChip pct={data.dollarIndex.changePercent} />
            </div>
          )}
          {data.fx.map((f) => {
            const usdStronger = f.changePercent === 0 ? null : f.usdUpWhenHigher === f.changePercent > 0
            return (
              <div key={f.pair} className="bg-slate-100/70 dark:bg-slate-900/50 rounded-lg p-2.5">
                <p className="text-[11px] text-slate-500 dark:text-slate-400">{f.label}</p>
                <p className="text-sm font-semibold text-slate-900 dark:text-white">
                  {f.price >= 100 ? f.price.toFixed(2) : f.price.toFixed(4)}
                </p>
                <div className="flex items-center gap-1.5">
                  <ChangeChip pct={f.changePercent} />
                  {usdStronger != null && (
                    <span className="text-[10px] text-slate-500 dark:text-slate-400">
                      USD {usdStronger ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Rates & vol context */}
      <div className="mt-3 flex flex-wrap gap-2">
        {data.markets.map((m) => (
          <div key={m.label} className="flex items-center gap-2 bg-slate-100/70 dark:bg-slate-900/50 rounded-lg px-3 py-2">
            <span className="text-[11px] text-slate-500 dark:text-slate-400">{m.label}</span>
            <span className="text-sm font-semibold text-slate-900 dark:text-white">
              {m.value != null ? `${m.value.toFixed(2)}${m.unit}` : '—'}
            </span>
            <ChangeChip pct={m.changePercent} />
            <LiveBadge live={m.live} />
          </div>
        ))}
      </div>

      {/* Suggested additions */}
      <div className="mt-4 border-t border-slate-200/70 dark:border-slate-700/50 pt-3">
        <p className="text-xs font-semibold text-slate-900 dark:text-white mb-2">
          Suggested indicators to add next
        </p>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {data.suggested.map((s) => (
            <div key={s.id} className="text-[11px] leading-snug">
              <span className="font-medium text-slate-700 dark:text-slate-200">{s.label}</span>
              <span className="text-slate-500 dark:text-slate-400"> — {s.why}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
